import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, AbstractControlOptions, AsyncValidatorFn, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { DistanceUnit } from '@models/enums/distance-unit';
import { GraphQLFormattedError } from 'graphql';
import { ActiveStudyExpenses, GetReimbursementRequestQuery } from 'graphql/generated';
import { ParseError, parsePhoneNumber } from 'libphonenumber-js';
import { DateObjectUnits, DateTime, DateTimeOptions } from 'luxon';
import { Observable, Subject } from 'rxjs';
import { PersistentFormGroup } from 'src/app/common/persistent-forms/persistent-form-group';
import { GraphQLErrorWithCode } from '../extensions/apollo-error';
import { ExpenseTypeEnum } from '../models/enums/expense-type-enum';
export interface WithErrorMessage {
    errorMessage$: Observable<string>;
}
export function withErrorMessage<T>(
    validatorFn: (control: AbstractControl) => T | null,
    errorMessageFn: (validationErrors: T) => Observable<string>
): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const validationResult = validatorFn(control);

        if (validationResult) {
            const errorMessage$ = errorMessageFn(validationResult);
            return addErrorMessage(validationResult, errorMessage$);
        }
        return null;
    };
}
export function addErrorMessage<T>(validationResult: NonNullable<T>, errorMessage$: Observable<string>) {
    const firstPropertyName = Object.keys(validationResult)[0] as keyof T;
    const firstPropertyValue: any = validationResult[firstPropertyName];
    if (firstPropertyValue) {
        if (typeof firstPropertyValue === 'object') {
            firstPropertyValue['errorMessage$'] = errorMessage$;
        } else {
            validationResult[firstPropertyName] = {
                value: firstPropertyValue,
                errorMessage$: errorMessage$,
            } as any;
        }
    }
    return validationResult;
}
export function parseApolloError(error: unknown): GraphQLErrorWithCode[] {
    if (CombinedGraphQLErrors.is(error)) {
        return error.errors.map((err: GraphQLFormattedError) => ({
            message: err.message,
            code: err.extensions ? (err.extensions['code'] as string) : '',
        }));
    }
    let graphQLErrors: GraphQLFormattedError[] | undefined;
    if (ServerError.is(error)) {
        try {
            graphQLErrors = JSON.parse(error.bodyText)?.errors;
        } catch {
            graphQLErrors = undefined;
        }
    } else if (error instanceof HttpErrorResponse) {
        graphQLErrors = error.error?.errors;
    }
    if (graphQLErrors?.length) {
        return graphQLErrors.map((err) => ({
            message: err.message,
            code: err.extensions ? (err.extensions['code'] as string) : '',
        }));
    }
    return [];
}
export function getFormData(value: any, formData = new FormData(), parentKey = ''): FormData {
    if (value === null || value === undefined) {
        formData.append(parentKey, '');
    } else if (typeof value === 'object') {
        if (value instanceof File) {
            formData.append(parentKey, value);
        } else if (Array.isArray(value)) {
            value.forEach((item, index) => {
                const key = `${parentKey}[${index}]`;
                getFormData(item, formData, key);
            });
        } else {
            for (const key in value) {
                if (value.hasOwnProperty(key)) {
                    const nestedKey = parentKey ? `${parentKey}.${key}` : key;
                    getFormData(value[key], formData, nestedKey);
                }
            }
        }
    } else {
        formData.append(parentKey, value);
    }
    return formData;
}
export abstract class AsyncModelFormGroup<
    TControl extends {
        [K in keyof TControl]: AbstractControl<any>;
    } = any,
    TModel = any,
> extends FormGroup<TControl> {
    protected destroy$ = new Subject<void>();
    destroy() {
        this.destroy$.next();
    }
    public model: TModel | null = null;
    constructor(
        controls: TControl,
        validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
        asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
    ) {
        super(controls, validatorOrOpts, asyncValidator);
    }
    public setModel(model: TModel) {
        this.model = model;
        return this.setValueFromModel(model);
    }
    abstract setValueFromModel(model: TModel): Observable<any>;
    abstract getModelFromValue(model: TModel): Observable<TModel>;
}

export abstract class ModelFormGroup<
    TControl extends {
        [K in keyof TControl]: AbstractControl<any>;
    } = any,
    TModel = any,
> extends PersistentFormGroup<TControl> {
    constructor(
        controls: TControl,
        validatorOrOpts?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
        asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
    ) {
        super(controls, validatorOrOpts, asyncValidator);
    }
    abstract setValueFromModel(model: TModel): void;
    abstract getModelFromValue(model: TModel): TModel;
}

/**
 *
 * @param phoneNumber string that contains a phone number
 * @returns a phone number in E.164 standard if phone number is valid, else input phone number
 */
export function tryFormatPhoneNumberToE164(phoneNumber: string): string {
    let parsedPhoneNumber;
    try {
        parsedPhoneNumber = parsePhoneNumber(phoneNumber);
    } catch (error) {
        if (error instanceof ParseError) console.debug(error);
        else throw error;
    }

    let result = parsedPhoneNumber?.number ?? phoneNumber;
    if (result.length > 15) {
        result = result.replace(/[\s+-/(/)]/g, '');
    }
    return result;
}

export function transformToDateTime(
    value: string,
    format: string = 'yyyy-MM-dd',
    time: DateObjectUnits = { hour: 0, minute: 0, second: 0, millisecond: 0 },
    opts: DateTimeOptions | undefined = undefined
): DateTime {
    return DateTime.fromFormat(value, format, opts).set(time);
}
export function isMileageExpense(expenseType: ActiveStudyExpenses | null | undefined) {
    return expenseType?.expenseId === ExpenseTypeEnum.Mileage;
}
export function isMileageCategory(expenseId: number) {
    return +expenseId === ExpenseTypeEnum.Mileage;
}
export function getSiteAddress(
    site: Partial<{
        address1: string;
        address2: string | null;
        city: string;
        zipcode: string | null;
        state?: string | null;
        country: string;
    }>
): string {
    return [site.address1, site.address2, site.city, site.zipcode, site.state, site.country].filter(Boolean).join(' ');
}

export function parseDistanceUnit(input: string | null | undefined): DistanceUnit | null {
    if (!input) return null;
    return ['m', 'mi', 'miles'].includes(input.toLowerCase())
        ? DistanceUnit.Miles
        : ['k', 'km', 'kilometers', 'kilometres'].includes(input.toLowerCase())
          ? DistanceUnit.Kilometers
          : null;
}
export function stringifyDistanceUnit(unit: DistanceUnit | null) {
    if (!unit) return '';
    else if (unit === DistanceUnit.Kilometers) return 'km';
    else if (unit === DistanceUnit.Miles) return 'mi';
    else return '';
}
export function serializeDistanceUnit(distanceUnit: DistanceUnit) {
    if (distanceUnit === DistanceUnit.Kilometers) return 'k';
    else if (distanceUnit === DistanceUnit.Miles) return 'm';
    else return '';
}

export function isNotEmpty(value: any): boolean {
    if (value === null || value === undefined) return false;

    if (Array.isArray(value) && value.length === 0) return false;

    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;

    return true;
}
export type ReimbursementRequestQuery = NonNullable<GetReimbursementRequestQuery['reimbursementRequest']>;
export type Transaction = ReimbursementRequestQuery['reimbursementTransaction'][number];
